import React, { useMemo } from "react";
import { Rubric } from "../pages/pray/Rubric";
import { Spacer } from "../pages/pray/Spacer";
import { useTweaks } from "../utils/TweakProvider";
import { PSALMS_1928 } from "./data-1928";
import { DEFAULT_TONES } from "./default-tones";
import { PsalmSection } from "./PsalmSection";
import {
    getEasierTone,
    getPointedPsalm,
    getTextFromPsalmRef,
    PsalmRef,
} from "./utils";

export const Psalm: React.FC<{
    psalmRef: PsalmRef;
    excludeGloriaPatri?: boolean;
    exportLines?: boolean;
}> = (props) => {
    const { psalmRef, excludeGloriaPatri, exportLines } = props;

    const toneRanges = DEFAULT_TONES[psalmRef.number];

    const { tweaks } = useTweaks();

    const sections = useMemo(() => {
        const psalmLength =
            PSALMS_1928.find((datum) => datum.id === `psalm${psalmRef.number}`)
                ?.lines.length ?? 1000;

        const sectionRefs: PsalmRef[] =
            toneRanges.length === 1
                ? [psalmRef]
                : toneRanges.map((toneRange, toneRangeIndex) => {
                      return {
                          type: "section",
                          number: psalmRef.number,
                          firstVerse:
                              psalmRef.type === "section"
                                  ? Math.max(
                                        psalmRef.firstVerse,
                                        toneRange.fromVerse
                                    )
                                  : toneRange.fromVerse,
                          lastVerse:
                              psalmRef.type === "section"
                                  ? Math.min(
                                        psalmRef.lastVerse,
                                        (toneRanges[toneRangeIndex + 1]
                                            ?.fromVerse ??
                                            psalmRef.lastVerse + 1) - 1
                                    )
                                  : (toneRanges[toneRangeIndex + 1]
                                        ?.fromVerse ?? psalmLength + 1) - 1,
                      };
                  });

        let visibleParts = 0;

        const sectionVisibility = sectionRefs.map((sectionRef) => {
            return !(
                sectionRef.type === "section" &&
                sectionRef.lastVerse - sectionRef.firstVerse <= 0
            );
        });

        return sectionRefs.map((sectionRef, sectionIndex) => {
            if (
                sectionRef.type === "section" &&
                sectionRef.lastVerse - sectionRef.firstVerse <= 0
            ) {
                return <></>;
            }

            visibleParts++;

            try {
                const pointedPsalmSection = getPointedPsalm(
                    sectionRef,
                    tweaks[getTextFromPsalmRef(sectionRef)] ??
                        toneRanges[sectionIndex].tone,
                    !(
                        sectionVisibility
                            .slice(sectionIndex + 1)
                            .indexOf(true) >= 0
                    ) && !excludeGloriaPatri
                );

                return (
                    <PsalmSection
                        sectionRef={sectionRef}
                        pointedPsalmSection={pointedPsalmSection}
                        title={
                            visibleParts > 1 ? (
                                <>
                                    Ps{" "}
                                    {psalmRef.type === "full"
                                        ? `${psalmRef.number}`
                                        : `${psalmRef.number}:${psalmRef.firstVerse}-${psalmRef.lastVerse}`}{" "}
                                    cont'd
                                </>
                            ) : (
                                <>
                                    Psalm{" "}
                                    {psalmRef.type === "full"
                                        ? `${psalmRef.number}`
                                        : `${psalmRef.number}:${psalmRef.firstVerse}-${psalmRef.lastVerse}`}
                                </>
                            )
                        }
                        tones={[
                            toneRanges[sectionIndex].tone,
                            getEasierTone(toneRanges[sectionIndex].tone),
                        ]}
                        exportLines={exportLines}
                    />
                );
            } catch (error) {
                return (
                    <div
                        style={{
                            background: "#db281b",
                            color: "white",
                            padding: 16,
                        }}
                    >
                        {error.message}
                    </div>
                );
            }
        });
    }, [tweaks]);

    return (
        <>
            {psalmRef.number === 95 ? (
                <Rubric
                    content="It may be appropriate to omit Psalm 95 here as it is used as
                the Invitatory Psalm at Mattins."
                />
            ) : (
                <></>
            )}
            {sections}
            <Spacer />
        </>
    );
};
